import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import buildStatusHistoryEntry from './buildStatusHistoryEntry'
import planParticipationCompletion from '../households/planParticipationCompletion'

/**
 * Legt één statuswissel van een huishouden of deelnemer vast in
 * `StatusHistory`. ELKE plek in de frontend die `Adresses.Status` of
 * `Personas.Status` schrijft roept dit aan — er zijn er tien, en een gemiste
 * plek betekent een gat in de doorlooptijd dat niet meer te herstellen is.
 * De server maakt óók huishoudens en deelnemers aan (intakeformulier,
 * adreskoppeling, adresstap) en schrijft daar de beginregel zelf, via de
 * spiegel functions/statusHistory/buildStatusHistoryEntry.js. Een nieuwe
 * server-schrijver hoort dat ook te doen.
 * AI-CONTEXT: See docs/pitfalls.md#database-side-effect-written-only-by-the-frontend
 *
 * Doet niets wanneer er niets te loggen valt (zelfde status, ontbrekend id).
 * Faalt nooit hard: een mislukte logregel mag de statuswijziging zelf niet
 * tegenhouden — de fout gaat wel naar de console.
 *
 * @param {object} params
 * @param {'adress'|'persona'} params.entityType - Huishouden of deelnemer
 * @param {string} params.entityID - Adresses.ID of Personas.ID (niet de docid)
 * @param {string} [params.fromStatus] - Status ervoor; leeg bij aanmaken
 * @param {string} params.toStatus - Status erna
 * @param {string} [params.changedBy] - Users.ID van wie de wissel deed
 * @returns {Promise<void>}
 */
const recordStatusChange = async ({ entityType, entityID, fromStatus, toStatus, changedBy }) => {
    const entry = buildStatusHistoryEntry({
        client,
        entityType,
        entityID,
        fromStatus,
        toStatus,
        changedBy,
        changedAt: firebase.firestore.FieldValue.serverTimestamp(),
        newId: uuid,
    })

    if (!entry) return

    try {
        await db.collection('StatusHistory').add(entry)
    } catch (error) {
        console.error('Statuswissel kon niet worden vastgelegd', { entityType, entityID, toStatus }, error)
    }

    await syncParticipationCompletion({ entityType, entityID, toStatus })
}

/**
 * Zet of wist het afrondmoment op de deelname-rijen van dít huishouden, voor
 * DEZE organisatie. Hier en niet bij de tien aanroepers: hetzelfde recept op
 * tien plekken loopt gegarandeerd uiteen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Faalt nooit hard — de statuswissel zelf is al gelukt en mag niet alsnog als
 * mislukt lezen.
 * AI-CONTEXT: See docs/pitfalls.md#api-error-claims-nothing-was-saved
 *
 * @param {{entityType: string, entityID: string, toStatus: string}} params
 * @returns {Promise<void>}
 */
const syncParticipationCompletion = async ({ entityType, entityID, toStatus }) => {
    if (entityType !== 'adress' || !entityID) return

    try {
        // De sleutel staat op het huishouden; de deelname-rijen hangen eraan.
        // Op CompagnyID EN ID, nooit op ID alleen.
        // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
        const adressSnap = await db.collection('Adresses')
            .where('CompagnyID', '==', client)
            .where('ID', '==', entityID)
            .get()
        const adress = adressSnap.docs.map(doc => doc.data()).find(data => !data.Deleted)
        const householdKey = (adress?.HouseholdKey || '').trim()
        if (!householdKey) return

        // `Members array-contains` is de poort van deze collectie: zonder dat
        // filter is het resultaat niet leeg maar geweigerd.
        // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
        const rijenSnap = await db.collection('HouseholdParticipations')
            .where('Members', 'array-contains', client)
            .where('HouseholdKey', '==', householdKey)
            .get()
        const rijen = rijenSnap.docs.map(doc => ({ docid: doc.id, ...doc.data() }))

        const updates = planParticipationCompletion({
            toStatus,
            participations: rijen,
            organisation: client,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })

        await Promise.all(updates.map(({ docid, patch }) =>
            db.collection('HouseholdParticipations').doc(docid).update(patch)
        ))
    } catch (error) {
        console.error('Afrondmoment op de deelname-rij kon niet worden bijgewerkt', { entityID, toStatus }, error)
    }
}

export default recordStatusChange
